import React, { forwardRef } from "react";
import type { BaseProps } from "@purpur/common-types";
import { FieldErrorText } from "@purpur/field-error-text";
import { FieldHelperText } from "@purpur/field-helper-text";
import { Label } from "@purpur/label";
import { useColorScheme } from "@purpur/theme";
import c from "classnames/bind";

import styles from "./text-area.module.scss";

export const ResizeOption = {
  NONE: "none",
  BOTH: "both",
  HORIZONTAL: "horizontal",
  VERTICAL: "vertical",
} as const;

export type ResizeOption = (typeof ResizeOption)[keyof typeof ResizeOption];

export type TextAreaProps = Omit<BaseProps<"textarea">, "id"> & {
  id: string;
  /**
   * Use to render error message below the text area. The text area renders with error appearance.
   * */
  errorText?: string;
  /**
   * Use to give context about the text area's input. Renders below the text area.
   * */
  helperText?: string;
  /**
   * The label of the text area.
   * */
  label?: string;
  /**
   * The height of the text area, measured in number of rows.
   * */
  rows?: number;
  /**
   * The width of the text area, measured in number of cols.
   * */
  cols?: number;
  /**
   * Controls whether the text area can be resized, and in which direction.
   * */
  resize?: ResizeOption;
};

const cx = c.bind(styles);
const rootClassName = "purpur-text-area";

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      id,
      ["data-testid"]: dataTestId,
      className,
      errorText,
      helperText,
      label,
      rows = 3,
      cols = 90,
      resize = ResizeOption.BOTH,
      style,
      ...props
    },
    ref
  ) => {
    const [colorScheme] = useColorScheme();

    const getTestId = (name: string) => (dataTestId ? `${dataTestId}-${name}` : undefined);

    const helperTextId = helperText ? `${id}-helper-text` : undefined;

    const textAreaContainerClassnames = cx(`${rootClassName}__textarea-container`, {
      [`${rootClassName}__textarea-container--disabled`]: props.disabled,
      [`${rootClassName}__textarea-container--readonly`]: props.readOnly && !props.disabled,
    });

    return (
      <div className={cx(className, rootClassName)} data-testid={dataTestId}>
        {label && (
          <Label
            htmlFor={id}
            disabled={props.disabled}
            className={styles[`${rootClassName}__label`]}
            data-testid={getTestId("label")}
          >
            {props.required && <span aria-hidden>*</span>}
            {label}
          </Label>
        )}
        <div className={textAreaContainerClassnames}>
          <textarea
            {...props}
            ref={ref}
            id={id}
            data-testid={getTestId("textarea")}
            aria-describedby={props["aria-describedby"] || helperTextId}
            aria-invalid={props["aria-invalid"] || !!errorText}
            className={cx(
              `${rootClassName}__textarea`,
              `${rootClassName}__textarea--${colorScheme}`,
              {
                [`${rootClassName}__textarea--error`]: !!errorText,
              }
            )}
            rows={rows}
            cols={cols}
            style={{ resize, ...style }}
          />
          <div className={styles[`${rootClassName}__frame`]} />
        </div>
        {helperTextId && (
          <FieldHelperText data-testid={getTestId("helper-text")} id={helperTextId}>
            {helperText}
          </FieldHelperText>
        )}
        {errorText && (
          <FieldErrorText data-testid={getTestId("error-text")}>{errorText}</FieldErrorText>
        )}
      </div>
    );
  }
);

TextArea.displayName = "TextArea";
